import { last } from 'voca';
import {
  EntityTypes,
  GrowthPlanSessionStatus,
  GrowthPlanStatus,
} from './enums';
import { User } from './user';

export interface ScaleupGrowthPlan {
  growthPlanId: string;
  orgId: string;
  entityType: EntityTypes.growthPlan;
  created: number;
  lastUpdated: number;
  sessions: GrowthPlanSessions;
  status: GrowthPlanStatus;
  sessionCount?: number;
}

export interface GrowthPlanSessions {
  [key: string]: GrowthPlanSession;
}

export interface GrowthPlanSessionExtended {
  sessionNumber: number;
  title: string;
  description: string;
  duration: number;
}

export interface GrowthPlanSession {
  sessionId: string;
  date?: string;
  time?: string;
  timeZone?: string;
  status: GrowthPlanSessionStatus;
  guests?: Guest[];
  calendarId?: string;
  conferenceLink?: string;
  transcriptLink?: string;
  transcriptText?: string;
  preWork?: SessionWorkHolder;
  sessionOutcomes?: SessionOutcomeHolder;
  lastUpdated?: number;
}

export interface Guest {
  name?: string;
  email: string;
  userId?: string;
}

export interface AvailableTimes {
  [key: string]: string[];
}

export interface GrowthPlanMeetingRequest {
  date: string;
  time: string;
  timeZone: string;
  guests?: Guest[];
}

//JSONSchema for GrowthPlanMeetingRequest
export const GrowthPlanMeetingRequestSchema = {
  type: 'object',
  properties: {
    date: { type: 'string' },
    time: { type: 'string' },
    timeZone: { type: 'string' },
    guests: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          name: { type: 'string' },
          email: { type: 'string', format: 'email' },
          userId: { type: 'string' },
        },
        required: ['email'],
      },
    },
  },
  required: ['date', 'time', 'timeZone'],
  additionalProperties: false,
};

export interface SessionWorkHolder {
  lastUpdated: number;
  work: { [key: string]: SessionWork };
  completed: boolean;
}

export interface FileUpload {
  fileName: string;
  contentType: string;
}
export interface SessionWork {
  questionId: string;
  question: string;
  answer: string | string[];
  fileUpload?: FileUpload | FileUpload[];
  completed?: boolean;
}

//JSONSchema for SessionWorkHolder
export const SessionWorkHolderSchema = {
  type: 'object',
  properties: {
    lastUpdated: { type: 'number' },
    completed: { type: 'boolean' },
    work: {
      type: 'object',
      additionalProperties: {
        type: 'object',
        properties: {
          questionId: { type: 'string' },
          question: { type: 'string' },
          answer: {
            anyOf: [
              { type: 'string' },
              {
                type: 'array',
                items: { type: 'string' },
              },
            ],
          },
          fileUpload: {
            anyOf: [
              {
                type: 'object',
                properties: {
                  fileName: { type: 'string' },
                  contentType: { type: 'string' },
                },
                required: ['fileName', 'contentType'],
              },
              {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    fileName: { type: 'string' },
                    contentType: { type: 'string' },
                  },
                  required: ['fileName', 'contentType'],
                },
              },
            ],
          },
        },
        required: ['questionId', 'question', 'answer'],
      },
    },
  },
  required: ['lastUpdated', 'work', 'completed'],
  additionalProperties: false,
};

export interface SessionOutcomeHolder {
  lastUpdated: number;
  outcomes: { [key: string]: SessionOutcome };
  completed: boolean;
}
export interface SessionOutcome {
  questionId: number;
  question: string;
  answer: string | string[];
  completed?: boolean;
  notApplicable?: boolean;
}

//JSONSchema for SessionOutcomeHolder
export const SessionOutcomeHolderSchema = {
  type: 'object',
  properties: {
    lastUpdated: { type: 'number' },
    completed: { type: 'boolean' },
    outcomes: {
      type: 'object',
      additionalProperties: {
        type: 'object',
        properties: {
          questionId: { type: 'number' },
          question: { type: 'string' },
          answer: {
            anyOf: [
              { type: 'string' },
              {
                type: 'array',
                items: { type: 'string' },
              },
            ],
          },
        },
        required: ['questionId', 'question', 'answer'],
      },
    },
  },
  required: ['lastUpdated', 'outcomes', 'completed'],
  additionalProperties: false,
};

export interface SGPReportEmailMessage {
  user: User;
  reportLink: string;
}

export interface SGPEnrichmentMessage {
    supplierId: string;
    growthPlanId: string;
}

export interface SGPEnrichmentResultMessage {
    supplierId: string;
    growthPlanId: string;
    success: boolean;
    errorMessage?: string;
}